(function () {
  "use strict";

  const CFG = {
    opticsMatrixCSV:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vT47GcsDVhIaNQdvD8REuRUNhDXvZaN_HVTtQy5J5QAWvsWNdOBYNYyp2twHTzyu1SomtqNczvOjZmO/pub?gid=672719765&single=true&output=csv",

    opticsLegendCSV:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vT47GcsDVhIaNQdvD8REuRUNhDXvZaN_HVTtQy5J5QAWvsWNdOBYNYyp2twHTzyu1SomtqNczvOjZmO/pub?gid=301187311&single=true&output=csv",

    cacheTtlMs: 120000
  };

  function norm(value) {
    return (value || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function escapeHtml(value) {
    return String(value || "").replace(
      /[&<>"']/g,
      character =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;"
        })[character]
    );
  }

  function isBuilderPage() {
    return !!(
      document.body.classList.contains("is-builder") ||
      document.querySelector(
        "#product-builder, [data-cpb-root], .cpb-container, #cpb"
      ) ||
      location.pathname.includes("valor-duty-holster")
    );
  }

  /*
   * The compatibility spreadsheet uses individual firearm model columns.
   * The old VSF data grouped those individual models into these five families.
   */
  function mapFirearmHeaderToFinderGun(header) {
    const text = String(header || "")
      .replace(/^\uFEFF/, "")
      .replace(/\s+/g, " ")
      .trim();

    if (!text) {
      return "";
    }

    if (
      norm(text) === norm("GLOCK 9/40 DS") ||
      /\bG(?:LOCK\s*)?(17|19|19X|22|23|34|45|47)\b/i.test(text)
    ) {
      return "GLOCK 9/40 DS";
    }

    if (
      norm(text) === norm("SIG P320") ||
      /\bP320\b/i.test(text) ||
      /\bM17X?\b/i.test(text) ||
      /\bM18X?\b/i.test(text)
    ) {
      return "SIG P320";
    }

    if (
      norm(text) === norm("S&W M&P 2.0") ||
      /M\s*&?\s*P(?:\s*2\.0)?/i.test(text)
    ) {
      return "S&W M&P 2.0";
    }

    if (
      norm(text) === norm("WALTHER PDP") ||
      /\bPDP\b/i.test(text)
    ) {
      return "WALTHER PDP";
    }

    if (
      norm(text) === norm("FN 509") ||
      /\b509\b/i.test(text)
    ) {
      return "FN 509";
    }

    return "";
  }

  function parseCSV(text) {
    const rows = [];

    let field = "";
    let row = [];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const character = text[i];

      if (inQuotes) {
        if (character === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += character;
        }

        continue;
      }

      if (character === '"') {
        inQuotes = true;
      } else if (character === ",") {
        row.push(field);
        field = "";
      } else if (
        character === "\n" ||
        character === "\r"
      ) {
        if (field.length || row.length) {
          row.push(field);
          rows.push(row);

          field = "";
          row = [];
        }

        if (
          character === "\r" &&
          text[i + 1] === "\n"
        ) {
          i++;
        }
      } else {
        field += character;
      }
    }

    if (field.length || row.length) {
      row.push(field);
      rows.push(row);
    }

    return rows;
  }

  function cacheGet(key) {
    try {
      const raw = sessionStorage.getItem(key);

      if (!raw) {
        return null;
      }

      const stored = JSON.parse(raw);

      if (
        !stored ||
        Date.now() - stored.time > CFG.cacheTtlMs
      ) {
        return null;
      }

      return stored.value;
    } catch (error) {
      return null;
    }
  }

  function cacheSet(key, value) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          time: Date.now(),
          value
        })
      );
    } catch (error) {
      // The widget can still work without session storage.
    }
  }

  async function fetchCSV(url, cacheKey) {
    const cached = cacheGet(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await fetch(url, {
      mode: "cors",
      credentials: "omit"
    });

    if (!response.ok) {
      throw new Error(
        `Spreadsheet request failed with status ${response.status}`
      );
    }

    const rows = parseCSV(await response.text());

    cacheSet(cacheKey, rows);

    return rows;
  }

  function parseLegend(rows) {
    const legend = {};

    rows.slice(1).forEach(row => {
      const key = String(row[0] || "")
        .trim()
        .toUpperCase();

      if (!key) {
        return;
      }

      legend[key] = {
        title: String(row[1] || "").trim(),
        body: String(row[2] || "").trim()
      };
    });

    return legend;
  }

  function parseMatrixCell(rawValue) {
    const raw = String(rawValue || "").trim();

    if (!raw) {
      return null;
    }

    const pieces = raw
      .split(/[:+|,]/)
      .map(piece => piece.trim())
      .filter(Boolean);

    const type = String(pieces.shift() || "")
      .toUpperCase();

    /*
     * Only verified and limited fits should appear in the finder.
     * N, NT, ?, DUPE, and empty cells are ignored.
     */
    if (type !== "Y" && type !== "L") {
      return null;
    }

    let sizeCode = "";
    let legendKey = "";

    pieces.forEach(piece => {
      const value = piece.toUpperCase();

      if (
        !sizeCode &&
        (value === "SM" || value === "LG")
      ) {
        sizeCode = value;
        return;
      }

      if (!legendKey) {
        legendKey = value;
      }
    });

    if (!sizeCode) {
      return null;
    }

    return {
      type,
      size:
        sizeCode === "SM"
          ? "Small"
          : "Large",
      legendKey
    };
  }

  function matrixToFinderRows(
    matrixRows,
    legendRows
  ) {
    if (!matrixRows || !matrixRows.length) {
      console.error(
        "VSF: The optics spreadsheet returned no rows."
      );

      return [];
    }

    const cleanHeader = value =>
      String(value || "")
        .replace(/^\uFEFF/, "")
        .replace(/\s+/g, " ")
        .trim();

    const headers = matrixRows[0].map(cleanHeader);

    const findHeaderIndex = wanted =>
      headers.findIndex(
        header =>
          norm(header) === norm(wanted)
      );

    const accessoryIndex =
      findHeaderIndex("Accessory");

    const metaIndex =
      findHeaderIndex("Meta");

    const notesIndex =
      findHeaderIndex("Notes");

    if (accessoryIndex === -1) {
      console.error(
        "VSF: Could not find the Accessory column.",
        headers
      );

      return [];
    }

    const legend = parseLegend(legendRows);

    const ignoredHeaders = new Set([
      "accessory",
      "meta",
      "notes"
    ]);

    const firearmColumns = headers
      .map((header, index) => ({
        header,
        index,
        finderGun:
          mapFirearmHeaderToFinderGun(header)
      }))
      .filter(column => {
        return (
          column.header &&
          !ignoredHeaders.has(
            column.header.toLowerCase()
          ) &&
          column.finderGun
        );
      });

    if (!firearmColumns.length) {
      console.error(
        "VSF: No supported firearm columns were found.",
        headers
      );

      return [];
    }

    /*
     * Several spreadsheet columns can belong to the same old finder family,
     * such as G17, G19, and G45 all becoming GLOCK 9/40 DS.
     */
    const resultMap = new Map();

    matrixRows.slice(1).forEach(row => {
      const accessory =
        String(row[accessoryIndex] || "").trim();

      const meta =
        metaIndex >= 0
          ? String(row[metaIndex] || "").trim()
          : "";

      const opticDisplay = accessory || meta;

      if (!opticDisplay) {
        return;
      }

      const baseNote =
        notesIndex >= 0
          ? String(row[notesIndex] || "").trim()
          : "";

      firearmColumns.forEach(column => {
        const cell = parseMatrixCell(
          row[column.index]
        );

        if (!cell) {
          return;
        }

        const legendEntry =
          cell.legendKey &&
          legend[cell.legendKey]
            ? legend[cell.legendKey]
            : null;

        const legendNote =
          legendEntry && legendEntry.body
            ? legendEntry.body
            : "";

        const note = [
          baseNote,
          legendNote
        ]
          .filter(Boolean)
          .join(" ");

        const record = {
          gun_model: column.finderGun,
          optic_brand: "",
          optic_model: opticDisplay,
          shroud_size: cell.size,
          note,
          _gun: column.finderGun,
          _opticDisplay: opticDisplay,
          _key: norm(
            `${column.finderGun} ${opticDisplay}`
          )
        };

        const mapKey = norm(
          `${column.finderGun} ${opticDisplay}`
        );

        /*
         * Avoid showing the same optic repeatedly when several firearm
         * columns inside one family produce the same recommendation.
         */
        if (!resultMap.has(mapKey)) {
          resultMap.set(mapKey, record);
          return;
        }

        const existing = resultMap.get(mapKey);

        /*
         * If one spreadsheet model contains a note and another does not,
         * retain the note without creating a duplicate optic entry.
         */
        if (
          !existing.note &&
          record.note
        ) {
          existing.note = record.note;
        }

        /*
         * Log conflicting recommendations so they can be corrected in the
         * spreadsheet. The first recommendation remains in the finder.
         */
        if (
          existing.shroud_size !==
          record.shroud_size
        ) {
          console.warn(
            "VSF: Conflicting shroud sizes found for:",
            column.finderGun,
            opticDisplay,
            existing.shroud_size,
            record.shroud_size
          );
        }
      });
    });

    const rows = Array.from(
      resultMap.values()
    );

    console.log(
      `VSF: Loaded ${rows.length} firearm/optic recommendations.`
    );

    return rows;
  }

  function renderInto(host, rows) {
    host.innerHTML = `
      <div class="vsf-inner">
        <h3 class="vsf-title">
          Find Your Recommended Optic Shroud Size
        </h3>

        ${
          isBuilderPage()
            ? `
              <p class="vsf-subnote">
                Your choice of removable optic shroud is included with your Level 2 or 3 holster. We can’t test every optic. <b>If yours isn’t listed, check the "WILL MY OPTIC FIT" box above to confirm compatibility </b>or choose the closest match.
              </p>
            `
            : ""
        }

        <div class="vsf-row">
          <label
            class="vsf-label"
            for="vsf-gun"
          >
            Gun Model
          </label>

          <select
            id="vsf-gun"
            class="vsf-input"
          >
            <option value="">
              Select your gun model…
            </option>

            <option value="GLOCK 9/40 DS">
              GLOCK 9/40 DS
            </option>

            <option value="SIG P320">
              SIG P320
            </option>

            <option value="S&W M&P 2.0">
              S&amp;W M&amp;P 2.0
            </option>

            <option value="WALTHER PDP">
              WALTHER PDP
            </option>

            <option value="FN 509">
              FN 509
            </option>
          </select>
        </div>

        <div class="vsf-row">
          <label
            class="vsf-label"
            for="vsf-optic"
          >
            Optic
          </label>

          <div class="vsf-autocomplete">
            <input
              id="vsf-optic"
              class="vsf-input"
              type="text"
              placeholder="Start typing your optic (e.g., Trijicon RMR HD)…"
              autocomplete="off"
            >

            <ul
              class="vsf-suggestions"
              id="vsf-suggestions"
              role="listbox"
              aria-label="Optic suggestions"
            ></ul>
          </div>
        </div>

        <div class="vsf-cta">
          <div
            class="vsf-result"
            id="vsf-result"
            aria-live="polite"
          ></div>

          <button
            type="button"
            id="vsf-check"
            class="vsf-btn"
            aria-label="Check fit"
          >
            Check Fit
          </button>
        </div>

        <p class="vsf-help">
          We can’t test every combo. If yours isn’t listed, choose the closest match.
        </p>
      </div>
    `;

    const isBuilder =
      !!document.querySelector(
        "#product-builder"
      );

    const gunEl =
      host.querySelector("#vsf-gun");

    const opticEl =
      host.querySelector("#vsf-optic");

    const suggestionsEl =
      host.querySelector("#vsf-suggestions");

    const resultEl =
      host.querySelector("#vsf-result");

    const checkButton =
      host.querySelector("#vsf-check");

    if (
      !gunEl ||
      !opticEl ||
      !suggestionsEl ||
      !resultEl ||
      !checkButton
    ) {
      return;
    }

    if (isBuilder) {
      checkButton.style.display = "none";

      const gunRow =
        gunEl.closest(".vsf-row");

      if (gunRow) {
        gunRow.style.display = "none";
      }

      const opticRow =
        opticEl.closest(".vsf-row");

      if (opticRow) {
        opticRow.classList.add(
          "vsf-row-optic"
        );
      }
    }

    let opticsForGun = [];
    let activeIndex = -1;
    let debounceTimer = null;

    function rebuildOpticsForGun() {
      resultEl.innerHTML = "";
      opticEl.value = "";
      activeIndex = -1;

      const selectedGun =
        String(gunEl.value || "").trim();

      if (!selectedGun) {
        opticsForGun = [];
        closeSuggestions();
        return;
      }

      const seen = new Set();

      opticsForGun = rows
        .filter(
          row =>
            norm(row._gun) ===
            norm(selectedGun)
        )
        .map(row => row._opticDisplay)
        .filter(optic => {
          const key = norm(optic);

          if (!key || seen.has(key)) {
            return false;
          }

          seen.add(key);
          return true;
        })
        .sort((a, b) =>
          a.localeCompare(b)
        );

      closeSuggestions();
    }

    gunEl.addEventListener(
      "change",
      rebuildOpticsForGun
    );

    function scoreMatch(
      text,
      normalizedQuery
    ) {
      const normalizedText = norm(text);

      if (!normalizedQuery) {
        return 1;
      }

      const tokens = normalizedQuery
        .split(" ")
        .filter(Boolean);

      for (const token of tokens) {
        if (
          !normalizedText.includes(token)
        ) {
          return 0;
        }
      }

      let score = 1;

      if (
        normalizedText.startsWith(
          normalizedQuery
        )
      ) {
        score += 1;
      }

      const phraseIndex =
        normalizedText.indexOf(
          normalizedQuery
        );

      if (phraseIndex >= 0) {
        score += Math.max(
          0,
          0.6 - phraseIndex / 100
        );
      }

      return score;
    }

    function closeSuggestions() {
      suggestionsEl.style.display = "none";
      suggestionsEl.innerHTML = "";
      activeIndex = -1;
    }

    function openSuggestions(items) {
      suggestionsEl.innerHTML = items
        .map(item => {
          if (
            item === "No matches found" ||
            item ===
              "Select a firearm model first"
          ) {
            return `
              <li class="vsf-suggestion-message">
                ${escapeHtml(item)}
              </li>
            `;
          }

          return `
            <li
              role="option"
              data-optic="${escapeHtml(item)}"
              aria-selected="false"
            >
              ${escapeHtml(item)}
            </li>
          `;
        })
        .join("");

      suggestionsEl.style.display =
        "block";

      Array.from(
        suggestionsEl.querySelectorAll(
          "li[data-optic]"
        )
      ).forEach(item => {
        item.addEventListener(
          "mousedown",
          event => {
            event.preventDefault();

            selectSuggestion(
              item.dataset.optic
            );
          }
        );
      });
    }

    function renderSuggestions(query) {
      if (!gunEl.value) {
        openSuggestions([
          "Select a firearm model first"
        ]);

        return;
      }

      if (!opticsForGun.length) {
        openSuggestions([
          "No matches found"
        ]);

        return;
      }

      const normalizedQuery = norm(query);

      let list = opticsForGun;

      if (normalizedQuery) {
        list = opticsForGun
          .map(value => ({
            value,
            score: scoreMatch(
              value,
              normalizedQuery
            )
          }))
          .filter(item => item.score > 0)
          .sort(
            (a, b) =>
              b.score - a.score
          )
          .map(item => item.value);
      }

      if (isBuilder) {
        list = [
          "NONE",
          ...list.filter(
            value =>
              norm(value) !== "none"
          )
        ];
      }

      openSuggestions(
        list.length
          ? list
          : ["No matches found"]
      );
    }

    opticEl.addEventListener(
      "input",
      () => {
        resultEl.innerHTML = "";
        activeIndex = -1;

        clearTimeout(debounceTimer);

        debounceTimer = setTimeout(
          () => {
            renderSuggestions(
              opticEl.value
            );
          },
          120
        );
      }
    );

    function openOnFocus() {
      resultEl.innerHTML = "";
      activeIndex = -1;

      renderSuggestions(
        opticEl.value
      );
    }

    opticEl.addEventListener(
      "focus",
      openOnFocus
    );

    opticEl.addEventListener(
      "click",
      openOnFocus
    );

    function markActive(items) {
      items.forEach(
        (item, index) => {
          item.setAttribute(
            "aria-selected",
            index === activeIndex
              ? "true"
              : "false"
          );
        }
      );

      const active =
        items[activeIndex];

      if (active) {
        active.scrollIntoView({
          block: "nearest"
        });
      }
    }

    opticEl.addEventListener(
      "keydown",
      event => {
        if (
          suggestionsEl.style.display ===
          "none"
        ) {
          return;
        }

        const items = Array.from(
          suggestionsEl.querySelectorAll(
            "li[data-optic]"
          )
        );

        if (!items.length) {
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();

          activeIndex = Math.min(
            activeIndex + 1,
            items.length - 1
          );

          markActive(items);
        } else if (
          event.key === "ArrowUp"
        ) {
          event.preventDefault();

          activeIndex = Math.max(
            activeIndex - 1,
            0
          );

          markActive(items);
        } else if (
          event.key === "Enter" &&
          activeIndex >= 0
        ) {
          event.preventDefault();

          selectSuggestion(
            items[activeIndex].dataset.optic
          );
        } else if (
          event.key === "Escape"
        ) {
          closeSuggestions();
        }
      }
    );

    function findRowFor(
      gun,
      opticDisplay
    ) {
      const gunKey = norm(gun);
      const opticKey = norm(opticDisplay);

      const exact = rows.find(
        row =>
          norm(row._gun) === gunKey &&
          norm(row._opticDisplay) ===
            opticKey
      );

      if (exact) {
        return exact;
      }

      const tokens = opticKey
        .split(" ")
        .filter(Boolean);

      return rows.find(
        row =>
          norm(row._gun) === gunKey &&
          tokens.every(token =>
            norm(
              row._opticDisplay
            ).includes(token)
          )
      );
    }

    function selectSuggestion(text) {
      opticEl.value = text;

      closeSuggestions();
      resultEl.innerHTML = "";

      if (
        isBuilder &&
        norm(text) === "none"
      ) {
        showAnswer({
          shroud_size: "Small",
          note: ""
        });

        selectCPBSize("Small");

        return;
      }

      if (isBuilder) {
        instantResolveFor(text);
      }
    }

    function showAnswer(row) {
      const size =
        String(
          row.shroud_size || ""
        ).trim();

      const note =
        String(row.note || "").trim();

      const pillClass =
        size.toLowerCase() === "small"
          ? "small"
          : "large";

      resultEl.innerHTML = `
        <span
          class="vsf-pill ${pillClass}"
          role="status"
        >
          <svg
            class="vsf-pill-ico"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20.3 5.7a1 1 0 0 1 0 1.4l-10 10a1 1 0 0 1-1.4 0L3.7 12a1 1 0 1 1 1.4-1.4l4.1 4.1 9.3-9.3a1 1 0 0 1 1.4 0z"/>
          </svg>

          Recommended:
          <strong>
            ${escapeHtml(size || "—")}
          </strong>
        </span>

        ${
          note
            ? `
              <div class="vsf-note vsf-note--inline">
                ${escapeHtml(note)}
              </div>
            `
            : ""
        }
      `;
    }

    function selectCPBSize(size) {
      const wantedSize =
        String(size || "")
          .trim()
          .toLowerCase();

      if (!wantedSize) {
        return;
      }

      const category =
        host.closest(
          ".cpb-category-content"
        ) ||
        host.closest(
          '[class*="cpb-category"]'
        );

      const searchRoot =
        category || document;

      const container =
        searchRoot.querySelector(
          ".cpb-category-options-container"
        );

      if (!container) {
        console.warn(
          "VSF: Could not find the shroud option container."
        );

        return;
      }

      const getText = element =>
        String(
          element &&
            element.textContent
            ? element.textContent
            : ""
        )
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();

      const getActive = () =>
        container.querySelector(
          '.cpb-option.cpb-active, .cpb-active, [aria-pressed="true"]'
        );

      const current = getActive();

      if (
        current &&
        getText(current).includes(
          wantedSize
        )
      ) {
        return;
      }

      const options = Array.from(
        container.querySelectorAll(
          '.cpb-option[role="button"], .cpb-option, [role="button"]'
        )
      ).filter(option =>
        getText(option)
      );

      const target = options.find(
        option => {
          const text = getText(option);

          return (
            text === wantedSize ||
            text.includes(wantedSize)
          );
        }
      );

      if (!target) {
        console.warn(
          `VSF: Could not find the ${size} shroud option.`
        );

        return;
      }

      function fire(element) {
        [
          "pointerdown",
          "mousedown",
          "click",
          "mouseup"
        ].forEach(type => {
          element.dispatchEvent(
            new MouseEvent(type, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
        });
      }

      fire(target);

      const wrapper =
        target.closest(".option") ||
        target.parentElement;

      if (
        wrapper &&
        wrapper !== target
      ) {
        fire(wrapper);
      }

      let attempts = 0;

      function verify() {
        const active = getActive();

        if (
          active &&
          getText(active).includes(
            wantedSize
          )
        ) {
          return;
        }

        attempts++;

        if (attempts > 5) {
          return;
        }

        setTimeout(() => {
          fire(target);
          verify();
        }, 120);
      }

      verify();
    }

    function instantResolveFor(
      opticDisplay
    ) {
      const gun =
        String(gunEl.value || "").trim();

      if (!gun || !opticDisplay) {
        return;
      }

      if (
        isBuilder &&
        norm(opticDisplay) === "none"
      ) {
        showAnswer({
          shroud_size: "Small",
          note: ""
        });

        selectCPBSize("Small");

        return;
      }

      const row = findRowFor(
        gun,
        opticDisplay
      );

      if (!row) {
        resultEl.innerHTML = `
          <div class="vsf-note">
            We couldn’t find that combination. Please choose another optic or
            <a
              class="vsf-link"
              href="/pages/contact-us"
            >
              contact us
            </a>.
          </div>
        `;

        return;
      }

      showAnswer(row);

      if (isBuilder) {
        selectCPBSize(
          row.shroud_size
        );
      }
    }

    checkButton.addEventListener(
      "click",
      () => {
        const gun =
          String(
            gunEl.value || ""
          ).trim();

        const optic =
          String(
            opticEl.value || ""
          ).trim();

        if (!gun) {
          resultEl.innerHTML = `
            <div class="vsf-note">
              Please select your gun model.
            </div>
          `;

          return;
        }

        if (!optic) {
          resultEl.innerHTML = `
            <div class="vsf-note">
              Please enter your optic.
            </div>
          `;

          return;
        }

        instantResolveFor(optic);
      }
    );

    /*
     * Synchronize the hidden finder gun family with the firearm model
     * currently selected in the Custom Product Builder.
     */
    function readSelectedBuilderModel() {
      const builderRoot =
        document.querySelector(
          "#product-builder"
        ) ||
        document.querySelector(
          ".product-builder"
        ) ||
        document.body;

      const categories =
        builderRoot.querySelectorAll(
          '[class*="cpb-category"]'
        );

      for (const category of categories) {
        const title =
          category.querySelector(
            '.cpb-category-title, [class*="category-title"]'
          );

        if (
          !title ||
          !/model/i.test(
            title.textContent || ""
          )
        ) {
          continue;
        }

        const selected =
          category.querySelector(
            [
              ".cpb-option.cpb-active",
              ".cpb-active",
              '[aria-pressed="true"]',
              ".is-selected",
              ".selected",
              "input:checked"
            ].join(",")
          );

        if (!selected) {
          continue;
        }

        const label =
          selected.closest("label");

        const text =
          String(
            selected.textContent ||
              (label
                ? label.textContent
                : "") ||
              selected.value ||
              ""
          )
            .replace(/\s+/g, " ")
            .trim();

        if (text) {
          return text;
        }
      }

      return "";
    }

    function mapBuilderModelToFinderGun(
      modelText
    ) {
      const direct =
        mapFirearmHeaderToFinderGun(
          modelText
        );

      if (direct) {
        return direct;
      }

      const text = String(
        modelText || ""
      );

      if (
        /\b(G17|G19X|G19|G22|G23|G34|G45|G47)\b/i.test(
          text
        )
      ) {
        return "GLOCK 9/40 DS";
      }

      if (
        /\b(P320|M17|M18)\b/i.test(text)
      ) {
        return "SIG P320";
      }

      if (
        /M\s*&?\s*P/i.test(text)
      ) {
        return "S&W M&P 2.0";
      }

      if (/\bPDP\b/i.test(text)) {
        return "WALTHER PDP";
      }

      if (/\b509\b/i.test(text)) {
        return "FN 509";
      }

      return "";
    }

    function syncGunFromBuilder() {
      if (!isBuilder) {
        return;
      }

      const modelText =
        readSelectedBuilderModel();

      if (!modelText) {
        return;
      }

      const mappedGun =
        mapBuilderModelToFinderGun(
          modelText
        );

      if (
        !mappedGun ||
        gunEl.value === mappedGun
      ) {
        return;
      }

      const option = Array.from(
        gunEl.options
      ).find(item => {
        return (
          norm(item.value) ===
            norm(mappedGun) ||
          norm(item.textContent) ===
            norm(mappedGun)
        );
      });

      if (!option) {
        return;
      }

      gunEl.value = option.value;

      gunEl.dispatchEvent(
        new Event("change", {
          bubbles: true
        })
      );
    }

    if (isBuilder) {
      const builderRoot =
        document.querySelector(
          "#product-builder"
        ) ||
        document.querySelector(
          ".product-builder"
        ) ||
        document.body;

      syncGunFromBuilder();

      builderRoot.addEventListener(
        "click",
        () => {
          setTimeout(
            syncGunFromBuilder,
            0
          );
        },
        true
      );

      builderRoot.addEventListener(
        "change",
        () => {
          setTimeout(
            syncGunFromBuilder,
            0
          );
        },
        true
      );

      new MutationObserver(() => {
        syncGunFromBuilder();
      }).observe(builderRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "aria-pressed",
          "checked"
        ]
      });

      /*
       * Builder content can finish rendering after this script starts.
       */
      let syncAttempts = 0;

      const syncInterval =
        setInterval(() => {
          syncGunFromBuilder();

          syncAttempts++;

          if (syncAttempts > 20) {
            clearInterval(
              syncInterval
            );
          }
        }, 250);
    }
  }

  function materializeMarkers(doc) {
    if (!doc || !doc.body) {
      return;
    }

    try {
      const walker =
        doc.createTreeWalker(
          doc.body,
          NodeFilter.SHOW_TEXT,
          null
        );

      const nodes = [];

      while (walker.nextNode()) {
        const node = walker.currentNode;

        if (
          node.nodeValue &&
          node.nodeValue.includes(
            "[[VSF_SHROUD_FINDER]]"
          )
        ) {
          nodes.push(node);
        }
      }

      nodes.forEach(node => {
        const pieces =
          node.nodeValue.split(
            "[[VSF_SHROUD_FINDER]]"
          );

        const fragment =
          doc.createDocumentFragment();

        pieces.forEach(
          (text, index) => {
            if (text) {
              fragment.appendChild(
                doc.createTextNode(text)
              );
            }

            if (
              index <
              pieces.length - 1
            ) {
              const host =
                doc.createElement("span");

              host.className =
                "valor-shroud-finder";

              host.appendChild(
                doc.createTextNode(
                  "\u00A0"
                )
              );

              fragment.appendChild(host);
            }
          }
        );

        if (node.parentNode) {
          node.parentNode.replaceChild(
            fragment,
            node
          );
        }
      });
    } catch (error) {
      console.error(
        "VSF: Could not create the widget marker.",
        error
      );
    }
  }

  function mountAllInDocument(
    doc,
    rows
  ) {
    if (!doc) {
      return;
    }

    materializeMarkers(doc);

    doc.querySelectorAll(
      ".valor-shroud-finder[data-vsf], .valor-shroud-finder"
    ).forEach(host => {
      if (host.__vsfMounted) {
        return;
      }

      host.__vsfMounted = true;

      renderInto(host, rows);
    });
  }

  function mountInIframes(rows) {
    document
      .querySelectorAll("iframe")
      .forEach(iframe => {
        try {
          const doc =
            iframe.contentDocument;

          if (!doc || !doc.body) {
            return;
          }

          mountAllInDocument(
            doc,
            rows
          );

          if (
            !iframe.__vsfObserver
          ) {
            iframe.__vsfObserver =
              new MutationObserver(
                () => {
                  mountAllInDocument(
                    doc,
                    rows
                  );
                }
              );

            iframe.__vsfObserver.observe(
              doc.documentElement ||
                doc.body,
              {
                childList: true,
                subtree: true
              }
            );
          }
        } catch (error) {
          /*
           * Cross-origin iframes cannot be accessed.
           */
        }
      });
  }

  async function boot() {
    try {
      const [
        matrixRows,
        legendRows
      ] = await Promise.all([
        fetchCSV(
          CFG.opticsMatrixCSV,
          "vsf_optics_matrix_v2"
        ),

        fetchCSV(
          CFG.opticsLegendCSV,
          "vsf_optics_legend_v2"
        )
      ]);

      const rows =
        matrixToFinderRows(
          matrixRows,
          legendRows
        );

      if (!rows.length) {
        console.error(
          "VSF: No finder recommendations were generated from the spreadsheet."
        );
      }

      function mountAll() {
        mountAllInDocument(
          document,
          rows
        );

        mountInIframes(rows);
      }

      mountAll();

      new MutationObserver(() => {
        mountAll();
      }).observe(
        document.documentElement,
        {
          childList: true,
          subtree: true
        }
      );

      let mountAttempts = 0;

      const mountInterval =
        setInterval(() => {
          mountAll();

          mountAttempts++;

          if (mountAttempts > 20) {
            clearInterval(
              mountInterval
            );
          }
        }, 500);
    } catch (error) {
      console.error(
        "VSF: Unable to load the optic compatibility spreadsheets.",
        error
      );
    }
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot
    );
  } else {
    boot();
  }
})();